import { FeeTier, MarketType } from '@velocity-exchange/sdk';
import { cj } from 'src/utils/classNames';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { Open } from '@velocity-exchange/icons';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import React, { useEffect, useMemo, useState } from 'react';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';
import { getMarketFees } from 'src/utils/market';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import { useShallow } from 'zustand/react/shallow';
import useModal from 'src/components/Modals/hooks/useModal';
import useUserFeeTier from 'src/components/Modals/FeeTierModal/hooks/useUserFeeTier';
import IconButton from 'src/components/primitives/IconButton/IconButton';

interface Props {
	className?: string;
}

const FeeTierValueDisplay = React.memo(function FeeTierValueDisplay({
	className,
}: Props) {
	const selectedMarketId = useVelocityStore((s) => s.selectedMarket.marketId);
	const { show } = useModal();
	const userAccountClient = useVelocityAccountsStore(
		(s) => s.accounts[s.currentUserKey ?? '']?.client
	);
	const isMobileScreenSize = useIsMobileScreenSize();
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const [tradeSide, postOnly] = useVelocityStore(
		useShallow((s) => [s.tradeForm.side, s.tradeForm.postOnly])
	);
	const [feeTier, setFeeTier] = useState<FeeTier>();
	const { userVolumeTierIndex, isPromoLiftingUserTier } = useUserFeeTier();

	const isZeroFeesPromotion = getIsZeroFeesActiveForMarket({
		marketIndex: selectedMarketId.marketIndex,
		isPerp: selectedMarketId.isPerp,
	});

	useEffect(() => {
		let stale = false;

		const getFeeTier = async () => {
			try {
				// User stats hold the volume the tier is derived from, and
				// `getMarketFees` reads nothing until they report subscribed. Their
				// cached account can be populated before that flag flips, so wait on
				// the flag rather than on a read succeeding.
				const statsAccount = velocityClient?.getUserStats();

				if (statsAccount && !statsAccount.isSubscribed) {
					await statsAccount.subscribe();
				}

				if (stale) {
					return;
				}

				setFeeTier(userAccountClient?.getUserFeeTier(MarketType.PERP));
			} catch (err) {
				console.error('Error getting user fee tier', err);

				if (!stale) {
					setFeeTier(undefined);
				}
			}
		};

		getFeeTier();

		return () => {
			stale = true;
		};
	}, [userAccountClient, velocityClient]);

	const feeTierContent = useMemo(() => {
		// `getMarketFees` falls back to environment defaults rather than throwing
		// while the client is unsubscribed, so readiness has to be gated on, not
		// caught. A resolved `feeTier` covers the user stats half of it.
		if (!userAccountClient || !feeTier || !velocityClientIsReady) {
			return null;
		}

		// The tier alone omits the market's taker surcharge, which
		// `calculatePerpTakerFee` does charge.
		let marketFees;

		try {
			marketFees = getMarketFees(
				userAccountClient,
				selectedMarketId.marketType,
				selectedMarketId.marketIndex,
				velocityClient
			);
		} catch {
			return null;
		}

		const takerFeePctFormatted = parseFloat(marketFees.takerFeePct.toFixed(4));
		// Rebates come back positive. Maker fee intentionally formatted with a
		// different precision.
		const makerFeePctFormatted = parseFloat(
			(-marketFees.makerFeePct).toFixed(5)
		);

		// While a promo floors the user above the rung their volume earns, show
		// what they would be paying without it. Priced through the same call
		// with only the tier swapped, so the two figures can differ only by the
		// tier and not by a surcharge the UI forgot to apply.
		let volumeTierTakerFeePctFormatted: number | undefined;

		if (isPromoLiftingUserTier && selectedMarketId.isPerp) {
			const volumeTier =
				velocityClient?.getStateAccount()?.perpFeeStructure.feeTiers[
					userVolumeTierIndex
				];

			if (volumeTier) {
				try {
					volumeTierTakerFeePctFormatted = parseFloat(
						getMarketFees(
							userAccountClient,
							selectedMarketId.marketType,
							selectedMarketId.marketIndex,
							velocityClient,
							volumeTier
						).takerFeePct.toFixed(4)
					);
				} catch {
					// Leave it unset: the charged fee still displays on its own.
				}
			}
		}

		if (isZeroFeesPromotion) {
			return (
				<>
					<span className="text-text-secondary line-through">
						{postOnly ? makerFeePctFormatted : takerFeePctFormatted}%
					</span>
					<span className="text-star-yellow">
						0% {!isMobileScreenSize && '/ 0%'}
					</span>
				</>
			);
		}

		// The promo moves the taker fee only, since the maker rebate is flat
		// across tiers, so a post-only display has nothing to strike through.
		const showPromoSaving =
			volumeTierTakerFeePctFormatted !== undefined &&
			volumeTierTakerFeePctFormatted > takerFeePctFormatted &&
			!(isMobileScreenSize && postOnly);

		if (showPromoSaving) {
			return (
				<>
					<span className="text-text-secondary line-through">
						{volumeTierTakerFeePctFormatted}%
					</span>
					<span className="text-star-yellow">
						{takerFeePctFormatted}%
						{!isMobileScreenSize && ` / ${makerFeePctFormatted}%`}
					</span>
				</>
			);
		}

		if (!isMobileScreenSize) {
			return `${takerFeePctFormatted}% / ${makerFeePctFormatted}%`;
		}

		if (!postOnly) {
			return `${takerFeePctFormatted}%`;
		}

		return `${makerFeePctFormatted}%`;
	}, [
		selectedMarketId,
		velocityClient,
		velocityClientIsReady,
		userAccountClient,
		feeTier,
		tradeSide,
		isZeroFeesPromotion,
		isMobileScreenSize,
		postOnly,
		isPromoLiftingUserTier,
		userVolumeTierIndex,
	]);

	const handleClick = () => {
		show({ type: 'fee_tier', props: {} });
	};

	return (
		<div
			className={cj(
				'flex items-center justify-between typo-body-xs',
				className
			)}
		>
			<span className="text-text-secondary">Fees</span>
			<span className="flex items-center gap-x-xs text-text-default">
				{feeTierContent ?? '-'}
				{feeTierContent && (
					<IconButton
						size="sm"
						variant="tertiary"
						className="-m-sm p-sm"
						onClick={handleClick}
						icon={Open}
					/>
				)}
			</span>
		</div>
	);
});

export default FeeTierValueDisplay;
